import {
  defineComponent, onMounted, type PropType, ref, type Ref, watch, computed,
} from 'vue'
import moment, { type Moment } from 'moment'
import { type CalendarDay, useCalendar } from '../../composables'
import '../calendar.sass'

moment.locale('ru', {
  week: { dow: 1 },
})

export default defineComponent({
  props: {
    start: {
      type: Object as PropType<Moment|undefined>,
    },
    end: {
      type: Object as PropType<Moment|undefined>,
    },
    onSetStart: {
      type: Function as PropType<(date: Moment|undefined) => void>,
      required: true,
    },
    onSetEnd: {
      type: Function as PropType<(date: Moment|undefined) => void>,
      required: true,
    },
    disablePastDates: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const {
      date, days, prevMonth, prevYear, nextMonth, nextYear,
    } = useCalendar(
      props.start || moment(),
      { disablePastDates: props.disablePastDates },
    )

    const weekNumbers: Ref<number[]> = ref([])
    const getWeekNumbers = () => {
      const weeks = new Set(days.value.map((d) => d.date.week()))
      weekNumbers.value = Array.from(weeks)
    }

    const getInitialPeriodValues = (): Array<Ref<Moment|undefined>> => {
      if (!props.start) return [ref(undefined), ref(undefined)]

      const dates = days.value.filter((d) => d.date.week() === props.start?.week())
      return [ref(dates[0].date), ref(dates[dates.length - 1].date)]
    }

    const mouseWheelHandler = (e: WheelEvent) => {
      e.preventDefault()
      if (e.deltaY > 0) nextMonth()
      else prevMonth()
    }

    const today = moment()
    const hoverWeek: Ref<number|undefined> = ref(undefined)
    const [startOfPeriod, endOfPeriod] = getInitialPeriodValues()

    const monthName = computed(() => date.value.toDate()
      .toLocaleString('ru', { month: 'long' })
      .replace(/^./, (s) => s.toUpperCase()))

    const setPeriod = ({ date }: CalendarDay) => {
      const dates = days.value.filter((d) => d.date.week() === date.week() && !d.disabled)
      if (!dates.length) return

      startOfPeriod.value = dates[0].date
      endOfPeriod.value = dates[dates.length - 1].date
    }

    const setWeek = (week: number) => {
      const startOfWeek = moment().week(week).startOf('week').add(1, 'day')
      const day = days.value.find((d) => d.date.isSame(startOfWeek, 'day'))
      if (day) setPeriod(day)
    }

    watch(startOfPeriod, (to) => props.onSetStart && props.onSetStart(to))
    watch(endOfPeriod, (to) => props.onSetEnd && props.onSetEnd(to))
    watch(days, getWeekNumbers)
    onMounted(getWeekNumbers)

    return () => (
      <div class="ark-ui-calendar with-week-panel">
        <div class="ark-ui-calendar-head">
          <div class="ark-ui-calendar-month">
            <div class="ark-ui-calendar-arrow-button" onClick={prevMonth}>
              <div class="ark-ui-calendar-arrow-left" />
            </div>
            <div class="ark-ui-calendar-month-name">{monthName.value}</div>
            <div class="ark-ui-calendar-arrow-button" onClick={nextMonth}>
              <div class="ark-ui-calendar-arrow-right" />
            </div>
          </div>
          <div class="ark-ui-calendar-year">
            <div class="ark-ui-calendar-arrow-button" onClick={prevYear}>
              <div class="ark-ui-calendar-arrow-left" />
            </div>
            <div>{ date.value.format('YYYY') }</div>
            <div class="ark-ui-calendar-arrow-button" onClick={nextYear}>
              <div class="ark-ui-calendar-arrow-right" />
            </div>
          </div>
        </div>
        <div class="ark-ui-calendar-row">
          <div class="ark-ui-calendar-week-panel">
            <div class="ark-ui-calendar-week-panel-number faded">#</div>
            {weekNumbers.value.map((week) => (
              <div
                class={`ark-ui-calendar-week-panel-number faded 
                  ${startOfPeriod.value?.week() === week ? 'selected' : ''} 
                  ${hoverWeek.value === week ? 'in-period' : ''}`}
                onClick={() => setWeek(week)}
              >
                {week}
              </div>
            ))}
          </div>
          <div class="ark-ui-calendar-days" onWheel={mouseWheelHandler}>
            <div class="ark-ui-calendar-day faded">Пн</div>
            <div class="ark-ui-calendar-day faded">Вт</div>
            <div class="ark-ui-calendar-day faded">Ср</div>
            <div class="ark-ui-calendar-day faded">Чт</div>
            <div class="ark-ui-calendar-day faded">Пт</div>
            <div class="ark-ui-calendar-day faded">Сб</div>
            <div class="ark-ui-calendar-day faded">Вс</div>
            { days.value.map((day) => {
              const cls = []
              if (day.faded || day.disabled) cls.push('faded')
              if (
                (day.date.week() === hoverWeek.value && !day.disabled)
                || (day.date.isAfter(startOfPeriod.value, 'day') && day.date.isBefore(endOfPeriod.value, 'day'))
              ) cls.push('in-period')
              if (
                day.date.isSame(startOfPeriod.value, 'day')
                || (day.date.isSame(endOfPeriod.value, 'day') && !day.disabled)
              ) cls.push('selected')
              if (day.date.isSame(today, 'day')) cls.push('today')

              return (
                <div class={`ark-ui-calendar-day ${cls.join(' ')}`}>
                  <div
                    class="ark-ui-calendar-day-border"
                    onClick={() => setPeriod(day)}
                    onMouseenter={() => { hoverWeek.value = day.date.week() }}
                    onMouseleave={() => { hoverWeek.value = undefined }}
                  >
                    { day.date.format('D') }
                  </div>
                </div>
              )
            }) }
          </div>
        </div>
      </div>
    )
  },
})
